import React from 'react'
import classnames from 'classnames/bind'
import type { ShallowTaskWorkflowMilestone } from '@adc/sdk/types'
import { formatDate } from '../../utils/formatters'
import Text from '../elementals/text'
import { PopupTooltip } from '../overlays'
import styles from './styles.scss'
import { AutomationTarget } from '../../types'

const cx = classnames.bind(styles)

export const OFFER_DATE_FORMAT = 'M/D/YY'
export const OFFER_TIMESTAMP_FORMAT = 'M/D/YY h:mm:ss A'
const TIME_ONLY_FORMAT = 'h:mm:ss A'

interface MilestoneDateProps extends AutomationTarget {
  milestone: ShallowTaskWorkflowMilestone
  compact?: boolean
  /** When true, display full timestamp (date + time) instead of date only */
  showTimestamp?: boolean
  /**
   * When true, show only time in the label with full date+time in a tooltip on hover.
   * Takes precedence over showTimestamp when both are true.
   * Default: false (existing behavior preserved).
   */
  compactTime?: boolean
}

const MilestoneDate: React.FC<MilestoneDateProps> = ({
  milestone,
  elmId = 'milestone_date',
  compact,
  showTimestamp,
  compactTime,
}) => {
  const textSize = compact ? 'tiny' : 'small'
  const dateFormat = showTimestamp ? OFFER_TIMESTAMP_FORMAT : OFFER_DATE_FORMAT

  if (milestone.completed_date) {
    if (compactTime) {
      const timeLabel = formatDate(milestone.completed_date, TIME_ONLY_FORMAT)
      const fullLabel = `Completed: ${formatDate(
        milestone.completed_date,
        OFFER_TIMESTAMP_FORMAT
      )}`
      return (
        // @ts-ignore next
        <PopupTooltip
          hideOnScroll
          trigger={
            <Text
              elmId={elmId}
              size={textSize}
              className={cx('milestone-date', 'milestone-date-compact')}
            >
              {timeLabel}
            </Text>
          }
          content={
            <Text size='tiny' color='white'>
              {fullLabel}
            </Text>
          }
          position='top center'
          on='hover'
        />
      )
    }
    return (
      <Text elmId={elmId} size={textSize} className={cx('milestone-date')}>
        Completed: {formatDate(milestone.completed_date, dateFormat)}
      </Text>
    )
  }

  if (milestone.expected_completion_date) {
    if (compactTime) {
      const timeLabel = formatDate(
        milestone.expected_completion_date,
        TIME_ONLY_FORMAT
      )
      const fullLabel = `Expected: ${formatDate(
        milestone.expected_completion_date,
        OFFER_TIMESTAMP_FORMAT
      )}`
      return (
        // @ts-ignore next
        <PopupTooltip
          hideOnScroll
          trigger={
            <Text
              elmId={elmId}
              size={textSize}
              className={cx('milestone-date', 'milestone-date-compact')}
            >
              {timeLabel}
            </Text>
          }
          content={
            <Text size='tiny' color='white'>
              {fullLabel}
            </Text>
          }
          position='top center'
          on='hover'
        />
      )
    }
    return (
      <Text elmId={elmId} size={textSize} className={cx('milestone-date')}>
        Expected Completion:{' '}
        {formatDate(milestone.expected_completion_date, dateFormat)}
      </Text>
    )
  }

  if (milestone.last_updated_date) {
    if (compactTime) {
      const timeLabel = formatDate(
        milestone.last_updated_date,
        TIME_ONLY_FORMAT
      )
      const fullLabel = `Updated: ${formatDate(
        milestone.last_updated_date,
        OFFER_TIMESTAMP_FORMAT
      )}`
      return (
        // @ts-ignore next
        <PopupTooltip
          hideOnScroll
          trigger={
            <Text
              elmId={elmId}
              size={textSize}
              className={cx('milestone-date', 'milestone-date-compact')}
            >
              {timeLabel}
            </Text>
          }
          content={
            <Text size='tiny' color='white'>
              {fullLabel}
            </Text>
          }
          position='top center'
          on='hover'
        />
      )
    }
    return (
      <Text elmId={elmId} size={textSize} className={cx('milestone-date')}>
        Updated: {formatDate(milestone.last_updated_date, dateFormat)}
      </Text>
    )
  }

  return null
}

export default MilestoneDate
